import {
  faAddressBook,
  faPenToSquare,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { MemberProfileForm } from '@/profile/MemberProfileForm';
import { PageContent, PageHeader } from '@/settings/settingsStyles';
import { useProfileHelpPlacement } from '~/hooks/useHelpPlacements';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/profile/edit',
)({
  component: EditProfileRoute,
});

function EditProfileRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  // Keyed on the viewer's relation to the displayed member, as legacy's profile
  // screens share one help placement block.
  const placement = useProfileHelpPlacement(memberId);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const memberIdNum = Number.parseInt(memberId, 10);

  const {
    data: form,
    isLoading,
    error,
  } = api.getMemberProfileForm.useQuery(
    { path: { member_id: memberIdNum } },
    { enabled: Number.isFinite(memberIdNum) },
  );

  /*
   * Photo stays on the strip for a supervisor even though that screen refuses
   * them, as legacy offers the same dead link.
   */
  const mainTabs = [
    {
      title: t('actions.edit'),
      to: 'edit',
      icon: faPenToSquare,
    },
    {
      title: t('profile.profile-photo'),
      to: 'change-avatar',
      icon: faAddressBook,
    },
  ];

  if (isLoading) return <LoadingSpinner centered />;

  /*
   * `require_profile_editor` answers 404 for a missing or deleted member and
   * 403 for a viewer who can't edit; both mean there's no edit screen here for
   * this viewer. Legacy redirects to the member's page instead. An empty group
   * list means no field is writable, so there's likewise nothing to edit.
   */
  if (error || form === undefined || form.groups.length === 0) {
    return <NotFoundScreen />;
  }

  return (
    <PageContent>
      {showSuccessAlert && (
        <AlertBox variant="success">{t('profile.changes-saved')}</AlertBox>
      )}
      <PageHeader>
        <TitleWithQuestion
          title={t('actions.edit-profile')}
          placement={placement}
        />
        {/* The profile view, which renders every member type. */}
        <Link to="/members/$memberId/profile/" params={{ memberId }}>
          <WBSButton wide alternate icon={faUser}>
            {t('profile.view-my-profile')}
          </WBSButton>
        </Link>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar tabs={mainTabs} base="/members/$memberId/profile" />
      </div>
      <InfoSectionContainer>
        <MemberProfileForm
          groups={form.groups}
          memberId={form.member_id}
          onSaved={() => {
            setShowSuccessAlert(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      </InfoSectionContainer>
    </PageContent>
  );
}
